import React, { useMemo, useState } from 'react';
import { BubbleBadge, BubbleButton, BubbleTable, BubbleMergedVarHint } from '../BubbleUI';
import { getBasicTimeString } from '@go-shared/lib/event';
import { populate } from '@go-client/lib/content';
import { mapBy, notFalsy, notUndefined } from '@go-shared/lib/functional';
import { BubbleTableTemplate } from '../BubbleUI/BubbleTable';
import {
  isHiddenContentDueToBasicFilters,
  evaluateMcmEndpoint,
  evaluateMcmTruthyAggregate,
  toggleMcmEndpoint,
} from '@go-shared/lib/mcm';
import { mcmEndpointToIcon } from '@go-client/lib/mcm';
import { VenueTemplate } from '@go-shared/types/venueTemplate';
import { Plan } from '@go-shared/types/plan';
import { HourFormat } from '@go-shared/types/brand';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { DailySchedulePanelMode, GENERATE_TABLE_FIELDS } from './DailySchedulePanel';
import { Content } from '@go-shared/types/content';
import { BareContentTemplate, ContentTemplateType } from '@go-shared/types/contentTemplate';
import { ContentTemplateTranslation } from '@go-shared/types/translation';
import { evaluateMergedVars, hasAllMergedVarsSet } from '@go-shared/lib/mergedVars';
import { camelToSentenceCase } from '@go-shared/lib/string';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import Fuse from 'fuse.js';
import { hashStringToColor } from '@go-shared/lib/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { findIconDefinition } from '@fortawesome/fontawesome-svg-core';
import ContentsGlance from '../ContentsGlance';
import {
  isNotBasicContentTemplate,
  translateBareContentTemplate,
} from '@go-shared/lib/contentTemplate';
import { SailingDayBundle } from '@go-shared/types/sailing';
import { evaluateAudienceTruthyAggregate, evaluateAudienceZone } from '@go-shared/lib/audience';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';

interface ContentTableItem {
  id: string;
  type: ContentTemplateType;
  name: string;
  description?: string;
  subtitle?: string;
  startTime: string;
  endTime?: string;
  venue?: string;
  _data: {
    content: Content;
    contentTemplate: BareContentTemplate;
  };
}

interface DailyContentListPanelPropsBase {
  mode: DailySchedulePanelMode;
  locked: boolean;
  search: string;
  focusEdp: string;
  focusLang: string | undefined;
  focusZone: string | undefined;
  mcmVisibleEdps: string[];
  plan: Plan;
  hourFormat: HourFormat;
  venueTemplates: VenueTemplate[];
  contentTemplates: BareContentTemplate[];
  contentTemplateTranslations: ContentTemplateTranslation[];
  dayBundle?: SailingDayBundle;
  onUpdateContent: (content: Content, transferToTargetDay?: string) => void;
  onEditContent: (content: Content) => void;
  onDeleteContent: (contentId: string) => void;
}

type DailyContentListPanelProps = DailyContentListPanelPropsBase &
  React.HTMLAttributes<HTMLDivElement>;

function DailyContentListPanel(props: DailyContentListPanelProps) {
  const {
    mode,
    locked,
    search,
    focusEdp,
    focusLang,
    focusZone,
    mcmVisibleEdps,
    plan,
    hourFormat,
    venueTemplates,
    contentTemplates,
    contentTemplateTranslations,
    dayBundle,
    onUpdateContent,
    onDeleteContent,
    onEditContent,
    ...rest
  } = props;

  const serverInfo = useServerInfo();
  const { activeProperty: property } = useActiveProperty();

  const [expandedReqId, setExpandedReqId] = useState<string | undefined>(undefined);
  const [showDelete, setShowDelete] = useState<{ [key: string]: boolean }>({});

  const contentTemplatesMap = mapBy(contentTemplates, '_id');
  const contentTemplateTranslationsMap = mapBy(contentTemplateTranslations, 'refers');

  const venueTemplatesMap = mapBy(venueTemplates, '_id');

  function getItemStatus(item: ContentTableItem) {
    const focusVisible = evaluateMcmEndpoint(
      'visible',
      focusEdp,
      item._data.content.mcm,
      item._data.contentTemplate.mcm
    );

    const focusTrending = evaluateMcmEndpoint(
      'trending',
      focusEdp,
      item._data.content.mcm,
      item._data.contentTemplate.mcm
    );

    const zoneVisible = focusZone
      ? evaluateAudienceZone(
          'visible',
          focusZone,
          item._data.content.audience,
          item._data.contentTemplate.audience
        )
      : true;

    const allVisible =
      evaluateMcmTruthyAggregate(
        'visible',
        mode === 'mcm' ? mcmVisibleEdps : serverInfo.mcmEndpoints,
        item._data.content.mcm,
        item._data.contentTemplate.mcm
      ) &&
      evaluateAudienceTruthyAggregate(
        'visible',
        property.zones.map(z => z._id),
        item._data.content.audience,
        item._data.contentTemplate.audience
      );

    const allTrending = evaluateMcmTruthyAggregate(
      'trending',
      mode === 'mcm' ? mcmVisibleEdps : serverInfo.mcmEndpoints,
      item._data.content.mcm,
      item._data.contentTemplate.mcm
    );

    const visible = mode === 'focus' ? focusVisible && zoneVisible : allVisible;
    const trending = mode === 'focus' ? focusTrending : allTrending;

    const translActive = focusLang
      ? ((contentTemplateTranslationsMap[item._data.contentTemplate._id]?.transforms ?? {})[
          focusLang
        ]?.active ?? false)
      : !item._data.contentTemplate.notNative;

    const translCntTmpl = translateBareContentTemplate(
      item._data.contentTemplate,
      contentTemplateTranslationsMap[item._data.contentTemplate._id],
      focusLang
    );

    let varsActive: boolean | null = null;

    const defaultLangVarSet =
      hasAllMergedVarsSet(
        item._data.contentTemplate.name,
        undefined,
        serverInfo.defaultLanguage.key,
        item._data.content.mergedVars
      ) &&
      isNotBasicContentTemplate(item._data.contentTemplate) &&
      hasAllMergedVarsSet(
        item._data.contentTemplate.description,
        undefined,
        serverInfo.defaultLanguage.key,
        item._data.content.mergedVars
      );

    const focusLangVarSet =
      !translCntTmpl ||
      (hasAllMergedVarsSet(
        translCntTmpl.name,
        focusLang,
        serverInfo.defaultLanguage.key,
        item._data.content.mergedVars
      ) &&
        isNotBasicContentTemplate(translCntTmpl) &&
        hasAllMergedVarsSet(
          translCntTmpl.description,
          focusLang,
          serverInfo.defaultLanguage.key,
          item._data.content.mergedVars
        ));

    if (defaultLangVarSet) {
      varsActive = true;

      if (mode === 'focus' && focusLang && !focusLangVarSet) {
        varsActive = null;
      }
    } else {
      varsActive = false;
    }

    const hiddenFromBasicFilters = isHiddenContentDueToBasicFilters(item._data.contentTemplate);

    return {
      visible:
        visible !== false &&
        !hiddenFromBasicFilters &&
        (props.mode === 'focus' ? translActive : true),
      hiddenFromBasicFilters,
      expanded: item.id === expandedReqId,
      trending,
      translActive,
      varsActive,
    };
  }

  const tableItems: ContentTableItem[] = plan.content
    .map(c => {
      const ct = contentTemplatesMap[c.template];
      if (!ct) return undefined;

      const content = populate(c, ct, plan.day);
      const type = ct.type || 'basic';
      let venue = '';
      let startTime,
        endTime = '';

      const [st, et] = getBasicTimeString(content, hourFormat).split(' - ');
      startTime = st;
      endTime = et;

      if (type === 'offer' && c.venue) {
        const vt = venueTemplatesMap[c.venue];
        venue = vt?.name;
      }

      const name =
        mode === 'focus'
          ? evaluateMergedVars(
              ct.name,
              dayBundle,
              serverInfo.languages.map(l => l.key),
              focusLang,
              serverInfo.defaultLanguage.key,
              c.mergedVars
            )[0]
          : ct.name;

      let description: string | undefined = undefined;

      if (isNotBasicContentTemplate(ct)) {
        description =
          mode === 'focus'
            ? evaluateMergedVars(
                ct.description,
                dayBundle,
                serverInfo.languages.map(l => l.key),
                focusLang,
                serverInfo.defaultLanguage.key,
                c.mergedVars
              )[0]
            : ct.description;
      }

      const result: ContentTableItem = {
        id: c._id,
        type,
        name,
        description,
        subtitle: camelToSentenceCase(type),
        startTime,
        endTime,
        venue,
        _data: { content, contentTemplate: ct },
      };

      return result;
    })
    .filter(notUndefined)
    .sort((a, b) => {
      if (a.type === b.type) {
        return a.name.localeCompare(b.name);
      }

      //compare names alphabetically
      return a.type.localeCompare(b.type);
    });

  const fuse = useMemo(() => {
    return new Fuse(tableItems, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['name'],
    });
  }, [tableItems]);

  const fuseSearchResults = useMemo(() => {
    if (!search || !fuse) return undefined;
    return fuse.search(search).map(s => s.item);
  }, [fuse, search]);

  const filteredTableItems = fuseSearchResults || tableItems;

  const cntFields = GENERATE_TABLE_FIELDS<ContentTableItem>();

  function makeDefaultTemplateTdProps(
    data: ContentTableItem,
    fieldTdProps: React.TdHTMLAttributes<HTMLTableCellElement>
  ) {
    const { visible, expanded } = getItemStatus(data);

    return {
      ...fieldTdProps,
      className: [
        fieldTdProps && fieldTdProps.className ? fieldTdProps.className : undefined,
        expanded ? 'bg-bubblegum-lightest' : undefined,
        !visible ? 'italic text-slate-lighter' : undefined,
      ]
        .filter(notUndefined)
        .join(' '),
    };
  }

  const cntTemplates: Array<BubbleTableTemplate<ContentTableItem>> = [
    {
      key: 'time',
      tdProps: makeDefaultTemplateTdProps,
      render: item => {
        return (
          <>
            <div className="font-semibold whitespace-nowrap">{item.startTime}</div>
            {item.endTime && <div className="whitespace-nowrap">{item.endTime}</div>}
          </>
        );
      },
    },
    {
      key: 'hints',
      tdProps: makeDefaultTemplateTdProps,
      render: item => (
        <BubbleBadge color={hashStringToColor(item.type)} className="mt-1 text-left">
          {item.type}
        </BubbleBadge>
      ),
    },
    {
      key: 'title',
      tdProps: makeDefaultTemplateTdProps,
      render: item => {
        return (
          <>
            <div className="font-semibold">
              <BubbleMergedVarHint mergedVars={item._data.content.mergedVars} text={item.name} />
            </div>
            {item.venue && <div>{item.venue}</div>}
          </>
        );
      },
    },
    {
      key: 'actions-1',
      tdProps: (item, props) => {
        const result = makeDefaultTemplateTdProps(item, props);
        if (mode === 'default' || mode === 'mcm') {
          result.className += ' hidden';
        }
        return result;
      },
      render: item => {
        const { translActive, varsActive } = getItemStatus(item);

        let statuses = <></>;

        if (mode === 'focus') {
          statuses = (
            <>
              <BubbleBadge variant={translActive ? 'smoke' : 'crimson'} className="mt-1 mr-1">
                <FontAwesomeIcon
                  fixedWidth
                  icon={
                    translActive
                      ? 'language'
                      : findIconDefinition({ prefix: 'fak', iconName: 'solid-language-slash' })
                  }
                />
              </BubbleBadge>
              <BubbleBadge
                variant={varsActive ? 'smoke' : varsActive === null ? 'banana' : 'crimson'}
                className="mt-1"
              >
                <FontAwesomeIcon
                  fixedWidth
                  icon={
                    varsActive || varsActive === null
                      ? 'dollar'
                      : findIconDefinition({ prefix: 'fak', iconName: 'solid-dollar-sign-slash' })
                  }
                />
              </BubbleBadge>
            </>
          );
        }

        return <>{statuses}</>;
      },
    },
    {
      key: 'actions-2',
      tdProps: makeDefaultTemplateTdProps,
      render: item => {
        const { visible, trending, hiddenFromBasicFilters } = getItemStatus(item);

        let deleteActions = <></>;
        let edpActions = <></>;

        if (mode === 'default') {
          if (!locked) {
            deleteActions = (
              <>
                {!showDelete[item.id] && (
                  <BubbleButton
                    className="p-2-i hover-item"
                    variant="crimson"
                    inverse
                    onClick={() => {
                      setShowDelete({ ...showDelete, [item.id]: true });
                    }}
                    icon="trash-alt"
                  />
                )}
                {showDelete[item.id] && (
                  <>
                    <BubbleButton
                      className="p-2-i hover-item"
                      variant="crimson"
                      inverse
                      onClick={() => {
                        onDeleteContent(item.id);
                      }}
                      icon="trash-check"
                    />
                    <BubbleButton
                      className="p-2-i hover-item"
                      variant="slate"
                      inverse
                      onClick={() => {
                        setShowDelete({ ...showDelete, [item.id]: false });
                      }}
                      icon="trash-xmark"
                    />
                  </>
                )}
              </>
            );
          }
          edpActions = (
            <>
              <BubbleButton
                className="p-2-i"
                variant={visible === null ? 'banana' : visible ? 'navy' : 'smoke'}
                inverse
                icon={visible === null ? 'eye' : visible ? 'eye' : 'eye-slash'}
                onClick={() => {
                  const cnt = {
                    ...item._data.content,
                    mcm: toggleMcmEndpoint(
                      'visible',
                      'all',
                      serverInfo.mcmEndpoints,
                      item._data.content.mcm,
                      item._data.contentTemplate.mcm
                    ),
                  };
                  onUpdateContent(cnt);
                }}
              />
              <BubbleButton
                className="p-2-i"
                variant={trending === null ? 'banana' : trending ? 'crimson' : 'smoke'}
                inverse
                icon="fire"
                onClick={() => {
                  const cnt = {
                    ...item._data.content,
                    mcm: toggleMcmEndpoint(
                      'trending',
                      'all',
                      serverInfo.mcmEndpoints,
                      item._data.content.mcm,
                      item._data.contentTemplate.mcm
                    ),
                  };
                  onUpdateContent(cnt);
                }}
              />
            </>
          );
        } else if (mode === 'mcm') {
          /* LABS-4777: Always show hidden eye icon if basic filters would hide the content,
          regardless of the MCM status at the time*/
          edpActions = (
            <>
              {mcmVisibleEdps.map(edp => {
                const mcmVisible =
                  !hiddenFromBasicFilters &&
                  evaluateMcmEndpoint(
                    'visible',
                    edp,
                    item._data.content.mcm,
                    item._data.contentTemplate.mcm
                  );

                return (
                  <BubbleButton
                    key={edp}
                    className="p-2-i"
                    variant={mcmVisible ? 'navy' : 'smoke'}
                    inverse
                    icon={mcmEndpointToIcon(edp, mcmVisible)}
                    onClick={() => {
                      const evt = {
                        ...item._data.content,
                        mcm: toggleMcmEndpoint(
                          'visible',
                          edp,
                          serverInfo.mcmEndpoints,
                          item._data.content.mcm,
                          item._data.contentTemplate.mcm
                        ),
                      };
                      onUpdateContent(evt);
                    }}
                  />
                );
              })}
            </>
          );
        } else if (mode === 'focus') {
          const mcmVisible =
            !hiddenFromBasicFilters &&
            evaluateMcmEndpoint(
              'visible',
              focusEdp,
              item._data.content.mcm,
              item._data.contentTemplate.mcm
            );

          edpActions = (
            <BubbleButton
              key={focusEdp}
              className="p-2-i"
              variant={mcmVisible ? 'navy' : 'smoke'}
              inverse
              icon={mcmEndpointToIcon(focusEdp, mcmVisible)}
              onClick={() => {
                const cnt = {
                  ...item._data.content,
                  mcm: toggleMcmEndpoint(
                    'visible',
                    focusEdp,
                    serverInfo.mcmEndpoints,
                    item._data.content.mcm,
                    item._data.contentTemplate.mcm
                  ),
                };
                onUpdateContent(cnt);
              }}
            />
          );
        }
        return (
          <fieldset disabled={locked}>
            {deleteActions}
            {edpActions}
          </fieldset>
        );
      },
    },
    {
      key: 'minimizer',
      tdProps: makeDefaultTemplateTdProps,
      render: item => {
        return (
          <BubbleButton
            className="p-2-i"
            variant="navy"
            inverse
            icon={
              expandedReqId === item.id
                ? 'arrow-down-left-and-arrow-up-right-to-center'
                : 'arrow-up-right-and-arrow-down-left-from-center'
            }
            onClick={() => {
              setExpandedReqId(expandedReqId === item.id ? undefined : item.id);
            }}
          />
        );
      },
    },
  ];

  function tableDetailsRender(item: ContentTableItem) {
    return (
      <div className="pb-3 pt-2">
        <div className="bg-white border shadow px-4 py-3 relative">
          <ContentsGlance
            locked={locked}
            content={item._data.content}
            onUpdateContent={onUpdateContent}
            name={item.name}
            timeString={[item.startTime, item.endTime].filter(notFalsy).join(' - ')}
            description={item.description}
          />
          {!locked && (
            <BubbleButton
              variant="navy"
              inverse
              className="absolute top-0 right-0"
              icon="edit"
              onClick={() => {
                onEditContent(item._data.content);
              }}
            />
          )}
        </div>
      </div>
    );
  }

  const cntCountData = tableItems.map(getItemStatus);
  const visibleCount = cntCountData.filter(ti => ti.visible === true).length;
  const notVisibleCount = cntCountData.filter(ti => ti.visible === false).length;
  const partialVisibleCount = cntCountData.filter(ti => ti.visible === null).length;

  return (
    <BubbleTable
      {...rest}
      responsive
      slim
      striped
      hideHeader
      trClassName="hover-visible"
      className="shadow-none-i"
      fields={cntFields}
      items={filteredTableItems}
      templates={cntTemplates}
      detailsKey={expandedReqId}
      detailsRender={tableDetailsRender}
      detailsClassName="bg-bubblegum-lightest"
      onRowDblClick={c => {
        if (!locked) {
          onEditContent(c._data.content);
        }
      }}
      stickyHeader={37}
      showPretitle
      pretitleClassName="p-0-i"
      pretitleRender={() => (
        <div className="p-2 flex gap-2 flex-wrap items-center bg-eggshell border-b border-t">
          <BubbleBadge variant="navy" className="flex-shrink-0">
            <FontAwesomeIcon icon="list" className="mr-1" />
            <span>{visibleCount + partialVisibleCount + notVisibleCount}</span>
          </BubbleBadge>
          <BubbleBadge variant="banana" className="flex-shrink-0">
            <FontAwesomeIcon icon="eye" className="mr-1" />
            <span>{visibleCount + partialVisibleCount}</span>
          </BubbleBadge>
          <BubbleBadge variant="smoke" className="flex-shrink-0">
            <FontAwesomeIcon icon="eye-slash" className="mr-1" />
            <span>{notVisibleCount}</span>
          </BubbleBadge>
          {mcmVisibleEdps
            .filter(edp => (mode === 'focus' ? focusEdp === edp : true))
            .map(edp => (
              <BubbleBadge key={edp} variant="navy" className="flex-shrink-0">
                <FontAwesomeIcon icon={mcmEndpointToIcon(edp, true)} className="mr-1" />
                <span>
                  {
                    tableItems
                      .map(
                        ti =>
                          getItemStatus(ti).visible &&
                          evaluateMcmEndpoint(
                            'visible',
                            edp,
                            ti._data.content.mcm,
                            ti._data.contentTemplate.mcm
                          )
                      )
                      .filter(notFalsy).length
                  }
                </span>
              </BubbleBadge>
            ))}
        </div>
      )}
    />
  );
}

export default DailyContentListPanel;
